import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate } from 'react-i18next';
import classnames from 'classnames';
import TetherComponent from 'react-tether';
import Tether from 'tether';

import Icon from 'common/components/core/icon';
import { SpiderGeocoderMarker } from 'common/components/core/markers/GeocoderMarker';
import LatLngUtils from 'common/utils/LatLngUtils';
import { ClusterMarker } from 'common/components/core/markers/ClusterMarker';
import { memoize } from 'common/utils/Memoizer';
import DataUtils from 'common/utils/DataUtils';
import { animatedClusterMarkerFactory } from 'common/components/core/markers/AnimatedClusteredMarker';
import MapUtil from 'common/utils/MapUtil';

import theme from './SpiderMarker.scss';

const MARKER_HEIGHT = 48;
const MARKER_WIDTH = 48;

const SpiderMarkerFactory = ({
    SpiderPopupMarker = SpiderGeocoderMarker,
    spiderMarkerStyle = {
        position: 'absolute',
        width: MARKER_WIDTH,
        height: MARKER_HEIGHT,
        left: -MARKER_WIDTH / 2,
        top: -MARKER_HEIGHT / 2
    },
    options = {
        useTether: true
    }
}) => {
    const SpiderMarkerWithTether = ({
        $hover,
        color,
        markerIcon,
        clusterCount,
        cluster,
        theme,
        markerLabel,
        showPopupOnClick,
        t,
        childHovered,
        childSelectedForEdit,
        childBeingEdited,
        allChildrenDisabled,
        renderPopup,
        isPopUpVisible,
        showPopup
    }) => {
        const { onDisabledElementHover, onHoverOut } = cluster[0].child.props;
        return (
            <TetherComponent
                attachment="bottom center"
                targetAttachment="bottom center"
                renderElementTag="div"
                offset="6px 0"
                className={theme.tetherSpiderPopup}
                constraints={[{ to: 'scrollParent', attachment: 'together' }]}
                optimizations={{
                    gpu: false
                }}
            >
                <div
                    onClick={
                        showPopupOnClick && !allChildrenDisabled
                            ? showPopup
                            : null
                    }
                    style={spiderMarkerStyle}
                    className={classnames(theme.spiderWrap, {
                        [theme.popupVisible]: isPopUpVisible()
                    })}
                >
                    <ClusterMarker
                        markerIcon={markerIcon}
                        markerLabel={markerLabel}
                        clusterCount={clusterCount}
                        overrideStyle={{}}
                        theme={theme}
                        color={color}
                        $hover={$hover || childHovered}
                        childSelectedForEdit={childSelectedForEdit}
                        childBeingEdited={childBeingEdited}
                        isDisabled={allChildrenDisabled}
                        onDisabledElementHover={onDisabledElementHover}
                        onHoverOut={onHoverOut}
                        tooltipText={isPopUpVisible() ? null : t('clickToOpen')}
                    />
                </div>
                {renderPopup()}
            </TetherComponent>
        );
    };

    const SpiderMarkerWithoutTether = ({
        $hover,
        color,
        markerIcon,
        clusterCount,
        cluster,
        theme,
        markerLabel,
        showPopupOnClick,
        t,
        childHovered,
        childSelectedForEdit,
        childBeingEdited,
        allChildrenDisabled,
        renderPopup,
        isPopUpVisible,
        showPopup
    }) => {
        const { onDisabledElementHover, onHoverOut } = cluster[0].child.props;
        return (
            <div
                className={classnames(theme.noTether, {
                    [theme.popupOpen]: isPopUpVisible()
                })}
            >
                <div
                    onClick={
                        showPopupOnClick && !allChildrenDisabled
                            ? showPopup
                            : null
                    }
                    style={spiderMarkerStyle}
                    className={classnames(theme.spiderWrap, {
                        [theme.popupVisible]: isPopUpVisible()
                    })}
                >
                    <ClusterMarker
                        markerIcon={markerIcon}
                        markerLabel={markerLabel}
                        clusterCount={clusterCount}
                        overrideStyle={{}}
                        theme={theme}
                        color={color}
                        $hover={$hover || childHovered}
                        childSelectedForEdit={childSelectedForEdit}
                        childBeingEdited={childBeingEdited}
                        isDisabled={allChildrenDisabled}
                        onDisabledElementHover={onDisabledElementHover}
                        onHoverOut={onHoverOut}
                        tooltipText={isPopUpVisible() ? null : t('clickToOpen')}
                    />
                </div>
                {renderPopup()}
            </div>
        );
    };

    class SpiderMarker extends PureComponent {
        constructor(props) {
            super(props);
            this.handleDragDone = this.handleDragDone.bind(this);
            this.getLatLngToPixel = LatLngUtils.getLatLngToPixel;
            this.getPixelToLatLng = LatLngUtils.getPixelToLatLng;
            this.isPopUpVisible = this.isPopUpVisible.bind(this);
            this.isAnyChildSelected = memoize(MapUtil.isAnyChildSelected);
            this.isAnyChildHovered = memoize(MapUtil.isAnyChildHovered);
            this.areAllChildrenDisabled = memoize(
                MapUtil.areAllChildrenDisabled
            );
            this.isAnyChildSelectedForEdit = memoize(
                MapUtil.isAnyChildSelectedForEdit
            );
            this.isAnyChildBeingEdited = memoize(MapUtil.isAnyChildBeingEdited);
            this.state = {
                isAnyChildSelected: this.isAnyChildSelected(props.cluster),
                isPopupAttached: false,
                draggingMarkerIndex: null
            };
            this.timeout = null;
            this.dragListener = this.setupDragListener();
        }

        UNSAFE_componentWillReceiveProps(nextProps) {
            const { isAnyChildSelected } = this.state;
            const nextIsAnyChildSelected = this.isAnyChildSelected(
                nextProps.cluster
            );
            if (nextIsAnyChildSelected !== isAnyChildSelected) {
                this.setState({
                    isAnyChildSelected: nextIsAnyChildSelected
                });
            }
            if (!this.dragListener) {
                this.dragListener = this.setupDragListener();
            }
        }

        componentDidUpdate() {
            // Using timeout so that Tether is repositioned after animation and transitions have finished
            this.timeout = setTimeout(() => {
                Tether.position();
            }, 100);
        }

        componentWillUnmount() {
            clearTimeout(this.timeout);
            if (this.dragListener)
                google.maps.event.removeListener(this.dragListener);
        }

        setupDragListener = () => {
            if (this.props.map) {
                return this.props.map.addListener('drag', () => {
                    this.forceUpdate();
                });
            }
        };

        renderPopup = () => {
            const {
                $onMouseAllow,
                className,
                bboxMap,
                disableSpiderMarkerDrag,
                cluster
            } = this.props;
            const { draggingMarkerIndex, hoveredMarkerIndex } = this.state;
            var draggingMarker = !DataUtils.isEmpty(draggingMarkerIndex);
            var popupStyle = draggingMarker
                ? { padding: 0, overflow: 'visible' }
                : {};
            return (
                <div
                    className={classnames(theme.popupPortal, {
                        [className]: className,
                        [theme.popupOpen]: this.isPopUpVisible(),
                        [theme.draggingMarker]: draggingMarker
                    })}
                >
                    {this.isPopUpVisible() ? (
                        <Icon
                            icon="cancel"
                            theme={theme}
                            className={theme.closePopupIcon}
                            onClick={this.hidePopup}
                        />
                    ) : null}
                    <div
                        className={theme.popup}
                        style={popupStyle}
                        onDrop={this.onDrop}
                    >
                        {this.isPopUpVisible() ? (
                            <div className={theme.popupInner}>
                                <div
                                    className={theme.markerList}
                                    style={popupStyle}
                                >
                                    {cluster.map(point => {
                                        const childProps = point.child.props;
                                        var isVisible = true;
                                        if (
                                            draggingMarker &&
                                            childProps.id !==
                                                draggingMarkerIndex
                                        ) {
                                            isVisible = false;
                                        }
                                        const $hover =
                                            hoveredMarkerIndex ===
                                            childProps.id;
                                        return (
                                            <SpiderPopupMarker
                                                {...childProps}
                                                disableSpiderMarkerDrag={
                                                    disableSpiderMarkerDrag
                                                }
                                                isMarkerInSpider
                                                key={point.child.props.id}
                                                bboxMap={bboxMap}
                                                handleDragStart={
                                                    this.handleDragStart
                                                }
                                                handleDragDone={
                                                    this.handleDragDone
                                                }
                                                handleLocationSelect={
                                                    this.handleLocationSelect
                                                }
                                                isVisible={isVisible}
                                                onMouseEnter={this.onMouseEnter}
                                                onMouseLeave={this.onMouseLeave}
                                                $hover={$hover}
                                                $onMouseAllow={$onMouseAllow}
                                            />
                                        );
                                    })}
                                </div>
                                <div className={theme.popupArrow} />
                            </div>
                        ) : null}
                    </div>
                </div>
            );
        };
        showPopup = (...args) => {
            const { showPopup, id } = this.props;
            showPopup(id, ...args);
        };

        hidePopup = () => {
            const {
                disableHidePopupIfChildSelected,
                hidePopup,
                id
            } = this.props;
            const { isAnyChildSelected } = this.state;
            // for plan map, disbale hiding if any child is selected
            if (disableHidePopupIfChildSelected && isAnyChildSelected) {
                return;
            }
            hidePopup(id);
        };

        handleLocationSelect = ({ locationEntity }) => {
            const { handleLocationSelect } = this.props;
            handleLocationSelect && handleLocationSelect({ locationEntity });
        };

        handleDragStart = ({ id }) => {
            this.setState({
                draggingMarkerIndex: id
            });
        };

        handleDragDone(markerProps, screenPixel) {
            var latLng = this.getPixelToLatLng(
                this.props.bboxMap,
                this.props.map,
                screenPixel
            );
            this.setState({
                draggingMarkerIndex: null,
                isPopupAttached: false
            });
            markerProps.onChildMouseUp(
                markerProps.id,
                markerProps,
                latLng,
                true
            );
        }

        isPopUpVisible() {
            const { cluster, openSpiderOnChildHover } = this.props;
            const allChildrenDisabled = this.areAllChildrenDisabled(cluster);
            if (openSpiderOnChildHover && this.isAnyChildHovered(cluster)) {
                return true;
            }
            return (
                !allChildrenDisabled &&
                (this.state.isAnyChildSelected || this.props.isPopupAttached)
            );
        }

        onMouseEnter = index => {
            this.setState({ hoveredMarkerIndex: index });
        };

        onMouseLeave = () => {
            this.setState({ hoveredMarkerIndex: null });
        };

        render() {
            const { cluster } = this.props;
            const { useTether } = options;
            const childHovered = this.isAnyChildHovered(cluster);
            const childSelectedForEdit = this.isAnyChildSelectedForEdit(
                cluster
            );
            const childBeingEdited = this.isAnyChildBeingEdited(cluster);
            const allChildrenDisabled = this.areAllChildrenDisabled(cluster);

            return useTether ? (
                <SpiderMarkerWithTether
                    {...this.props}
                    childHovered={childHovered}
                    childSelectedForEdit={childSelectedForEdit}
                    childBeingEdited={childBeingEdited}
                    allChildrenDisabled={allChildrenDisabled}
                    renderPopup={this.renderPopup}
                    isPopUpVisible={this.isPopUpVisible}
                    showPopup={this.showPopup}
                />
            ) : (
                <SpiderMarkerWithoutTether
                    {...this.props}
                    childHovered={childHovered}
                    childSelectedForEdit={childSelectedForEdit}
                    childBeingEdited={childBeingEdited}
                    allChildrenDisabled={allChildrenDisabled}
                    renderPopup={this.renderPopup}
                    isPopUpVisible={this.isPopUpVisible}
                    showPopup={this.showPopup}
                />
            );
        }
    }
    return compose(themr('SpiderMarker', theme), translate())(SpiderMarker);
};

const DefaultSpiderMarker = SpiderMarkerFactory({});
const AnimatedSpiderMarker = animatedClusterMarkerFactory(DefaultSpiderMarker);
export {
    SpiderMarkerFactory,
    DefaultSpiderMarker as SpiderMarker,
    AnimatedSpiderMarker
};
